import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Radio, Result, Select, Skeleton, Switch, Typography, message } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { autonomyOptions, copilotFormValues, copilotUpdatePayload, type CopilotDTO } from "./siteCopilotModel";
import "./SiteCopilot.css";

const { Title, Text, Paragraph } = Typography;

const SiteCopilot: React.FC = () => {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { API_BASE_URL } = useGlobal();
  const [form] = Form.useForm();
  const preview = Form.useWatch([], form) || {};
  const [copilot, setCopilot] = useState<CopilotDTO>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [issues, setIssues] = useState<Array<{ path: string; message: string }>>([]);
  const draftKey = `hongmo:copilot-draft:${siteId}`;

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError("");
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/copilot`, { signal });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "Copilot 配置读取失败");
      const next: CopilotDTO = response.data.data;
      setCopilot(next);
      let values = copilotFormValues(next);
      try {
        const local = localStorage.getItem(draftKey);
        if (local) values = { ...values, ...JSON.parse(local) };
      } catch { /* ignore unavailable local storage */ }
      form.setFieldsValue(values);
    } catch (reason: any) {
      if (reason?.code !== "ERR_CANCELED") setError(reason.response?.data?.msg || reason.message || "Copilot 配置读取失败");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_BASE_URL, draftKey, form, siteId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const rememberDraft = (_: unknown, values: Record<string, unknown>) => {
    try { localStorage.setItem(draftKey, JSON.stringify(values)); } catch { /* form remains available */ }
  };

  const save = async () => {
    if (!copilot) return;
    let values: any;
    try { values = await form.validateFields(); } catch { return; } // 校验失败 antd 自动展示,吞掉 {errorFields} 拒绝
    setSaving(true);
    setIssues([]);
    try {
      const payload = copilotUpdatePayload(values, copilot.draftConfig);
      const response = await request.put(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/copilot`, payload);
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "Draft 保存失败");
      const validation = await request.post(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/copilot/validate`);
      if (validation.data?.code !== 200) throw new Error(validation.data?.msg || "Draft 校验失败");
      setCopilot(response.data.data);
      setIssues(validation.data.data?.issues || []);
      localStorage.removeItem(draftKey);
      if (validation.data.data?.valid) message.success("Copilot Draft 已保存并通过校验");
      else message.warning("Draft 已保存，但发布前仍有需要处理的配置");
    } catch (reason: any) {
      rememberDraft(null, values);
      const requestId = reason.response?.headers?.["x-request-id"];
      message.error(`${reason.response?.data?.msg || reason.message || "保存失败，已保留本地草稿"}${requestId ? `（请求 ${requestId}）` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !copilot) return <main className="site-product-page"><Skeleton active paragraph={{ rows: 14 }} /></main>;
  if (error || !copilot) return <main className="site-product-page"><Result status="error" title="无法读取 Copilot 配置" subTitle={error} extra={<Button onClick={() => void load()}>重试</Button>} /></main>;

  const autonomy = autonomyOptions.find((item) => item.value === preview.autonomyMode) || autonomyOptions[0];
  return (
    <main className="site-product-page copilot-page" aria-labelledby="copilot-page-title">
      <header className="site-product-page__heading">
        <div><Text type="secondary">网站助手</Text><Title id="copilot-page-title" level={2}>Copilot</Title><Paragraph type="secondary">编辑 Draft 并实时预览。保存不会直接改变生产版本。</Paragraph></div>
        <div><span>{copilot.publishedVersion > 0 ? `生产版本 v${copilot.publishedVersion}` : "尚未发布"}</span><Button onClick={() => navigate(`/sites/${encodeURIComponent(siteId)}/publish`)}>发布中心</Button><Button type="primary" size="large" loading={saving} onClick={() => void save()}>保存 Draft</Button></div>
      </header>
      {issues.length ? <Alert showIcon type="warning" message="发布前需要处理" description={<ul>{issues.map((issue) => <li key={`${issue.path}-${issue.message}`}>{issue.path}：{issue.message}</li>)}</ul>} /> : null}
      <div className="copilot-editor-layout">
        <Form form={form} layout="vertical" onValuesChange={rememberDraft} className="copilot-editor">
          <section>
            <header><span>1</span><div><Title level={4}>身份与风格</Title><Text type="secondary">访客首先感受到的名字、角色和语气</Text></div></header>
            <div className="copilot-form-grid">
              <Form.Item name="name" label="助手名称" rules={[{ required: true }, { max: 128 }]}><Input placeholder="AI 助手" /></Form.Item>
              <Form.Item name="role" label="角色" rules={[{ required: true }, { max: 128 }]}><Input placeholder="网站助手" /></Form.Item>
              <Form.Item name="tone" label="语气"><Select options={[{ value: "friendly", label: "友好" }, { value: "professional", label: "专业" }, { value: "concise", label: "简洁" }, { value: "warm", label: "温暖" }]} /></Form.Item>
              <Form.Item name="language" label="语言"><Select options={[{ value: "auto", label: "跟随访客" }, { value: "zh-CN", label: "简体中文" }, { value: "en-US", label: "English" }]} /></Form.Item>
            </div>
            <Form.Item name="introduction" label="开场介绍" rules={[{ max: 500 }]}><Input.TextArea rows={2} showCount maxLength={500} /></Form.Item>
          </section>

          <section>
            <header><span>2</span><div><Title level={4}>回答策略</Title><Text type="secondary">明确引用和“不知道”时的行为</Text></div></header>
            <div className="copilot-form-grid">
              <Form.Item name="citations" label="引用来源"><Select options={[{ value: "when_available", label: "有站点来源时展示" }, { value: "always", label: "始终要求站点来源" }]} /></Form.Item>
              <Form.Item name="unknownBehavior" label="知识不足时"><Select options={[{ value: "say_unknown", label: "明确说明不知道" }, { value: "handoff", label: "建议联系人工" }, { value: "common_knowledge", label: "允许常识回答并标注" }]} /></Form.Item>
            </div>
            <Form.Item name="additionalInstructions" label="附加指令" extra="附加指令不能覆盖系统安全规则、工具权限、确认策略或 AI 标识。" rules={[{ max: 2000 }]}><Input.TextArea rows={4} showCount maxLength={2000} placeholder="例如：优先使用产品文档中的正式名称。" /></Form.Item>
          </section>

          <section>
            <header><span>3</span><div><Title level={4}>任务执行方式</Title><Text type="secondary">默认全自动完成任务，访客无需逐步授权</Text></div></header>
            <div className="copilot-auto-summary">
              <i className="ri-flashlight-fill" aria-hidden="true" />
              <div><strong>连续执行，不打断访客</strong><span>读取、导航、填写草稿和已验证的可恢复操作会自动完成；访客可随时停止任务。</span></div>
              <span>推荐</span>
            </div>
            <Form.Item name="autonomyMode">
              <Radio.Group className="autonomy-options">{autonomyOptions.map((option) => <Radio.Button value={option.value} key={option.value}><strong>{option.label}</strong><small>{option.detail}</small></Radio.Button>)}</Radio.Group>
            </Form.Item>
            <Alert showIcon type="info" message="只在必要的最终提交点停下" description="付款、密码与验证码、不可逆删除、未知高风险写入不会自动执行；其余步骤不向访客索要授权。" />
          </section>

          <section>
            <header><span>4</span><div><Title level={4}>用户字段</Title><Text type="secondary">只允许站点身份注入中的最小业务字段，不接受密码或令牌</Text></div></header>
            <Form.Item name="userFields"><Checkbox.Group options={[{ value: "locale", label: "语言" }, { value: "role", label: "角色" }, { value: "membership", label: "会员等级" }, { value: "name", label: "显示名称" }, { value: "email", label: "邮箱" }]} /></Form.Item>
          </section>

          <section>
            <header><span>5</span><div><Title level={4}>失败转人工</Title><Text type="secondary">仅在回答不足或任务失败时展示明确出口</Text></div></header>
            <Form.Item name="handoffEnabled" label="启用转人工" valuePropName="checked"><Switch /></Form.Item>
            {preview.handoffEnabled ? <div className="copilot-form-grid">
              <Form.Item name="handoffChannel" label="方式"><Select options={[{ value: "url", label: "客服链接" }, { value: "email", label: "客服邮箱" }]} /></Form.Item>
              <Form.Item name="handoffTarget" label="目标" rules={[{ required: true, message: "启用转人工后必须填写目标" }]}><Input placeholder={preview.handoffChannel === "email" ? "support@example.com" : "https://example.com/support"} /></Form.Item>
              <Form.Item name="handoffLabel" label="按钮文字" rules={[{ max: 32 }]}><Input /></Form.Item>
            </div> : null}
            <Form.Item name="retentionDays" label="对话保留"><Select options={[{ value: 0, label: "不保留" }, { value: 7, label: "7 天" }, { value: 30, label: "30 天" }, { value: 90, label: "90 天" }]} /></Form.Item>
          </section>
        </Form>

        <aside className="copilot-preview" aria-label="Copilot 实时预览">
          <header><span>实时预览</span><b>{autonomy.label}</b></header>
          <div className="copilot-preview__window">
            <div className="copilot-preview__brand"><span><i className="ri-sparkling-2-fill" /></span><div><strong>{preview.name || "AI 助手"}</strong><small>AI 生成 · {preview.role || "网站助手"}</small></div></div>
            <p className="copilot-preview__intro">{preview.introduction || "你好，我可以帮你查找站内内容。"}</p>
            <div className="copilot-preview__question">这项功能应该怎么使用？</div>
            <div className="copilot-preview__answer">我会直接完成可以安全自动执行的步骤，并持续显示进度；你随时可以停止。</div>
            {preview.citations !== "always" ? <span className="copilot-preview__source"><i className="ri-links-line" />来源：网站帮助文档</span> : <span className="copilot-preview__source"><i className="ri-shield-check-line" />仅在找到来源时回答</span>}
            {preview.handoffEnabled ? <Button block>{preview.handoffLabel || "联系人工"}</Button> : null}
          </div>
          <Alert showIcon type="info" message={autonomy.detail} />
        </aside>
      </div>
    </main>
  );
};

export default SiteCopilot;
